// Property Executive Summary — one row per ACCOUNT per YEAR.
//
// Replaced src/pages/PropertyExecutiveSummary.js, which welded fetch, filter and render into one
// file. That page was deleted once this reconciled to the cent on 4343-019-027 / 2021 ($719,254.04).
//
// The fetch, the row assembly and the tax derivation all live in ../parcelYearRows, shared with
// Outstanding Appeals. What is specific to THIS report is only: which filters it offers, and how it
// summarises. That is the whole point of the split — see the header of that file.

import { PARCEL_YEAR_COLUMNS, fetchParcelYearScope, buildParcelYearRows, NOTICE_OPTIONS } from '../parcelYearRows';
import { toNumber, sumColumn, isEstimated, rawValue, decidedNote, totalLabel, PENDING, BASELINE_MIXED_FOOTNOTE } from '../format';
import { computeTotals } from '../viewModel';
import { assertRowFields } from '../grouping';
import { taxSavingsRows, taxSavingsSheets } from '../taxSavingsAppealSummary';
import { loadLevelNames } from '../../utils/levelNames';

// As-of date, LOCAL time — never toISOString(), which rolls the date west of Greenwich and would
// stamp a report with yesterday.
const asOfLabel = (d = new Date()) =>
  d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

// The title block the analyst's clients recognise. Row 1 blank, client, report title, as-of date,
// two blanks, then the header on row 7 — exportXlsx shifts the data and the autofilter to match.
const titleRows = (clientName, heading) => [null, clientName || '', heading, `As of ${asOfLabel()}`, null, null];

const AVERAGE_YEARS = 3;

export const propertyExecutiveSummary = {
  id: 'exec_summary',
  title: 'Property Executive Summary',
  description: 'Proposed vs. final values, reductions, tax savings, and appeal status — by account and year.',

  filters: [
    'client',
    'years',
    { key: 'basis', type: 'toggle', label: 'Basis', default: 'taxable',
      options: [{ value: 'taxable', label: 'Taxable' }, { value: 'market', label: 'Market' }] },
    { key: 'status', type: 'enum', label: 'Status', from: 'appeal_status' },
    { key: 'state', type: 'enum', label: 'State', from: 'state' },
    { key: 'property', type: 'enum', label: 'Property', from: 'property', picker: 'search', noun: 'properties' },
    ...NOTICE_OPTIONS,
    // The Excel layout (step 8 part 5b): Standard (as always) or Debbie's Tax Savings Appeal Summary
    // (src/reports/taxSavingsAppealSummary.js). The screen and the PDF stay Standard.
    { key: 'layout', type: 'toggle', label: 'Excel layout', default: 'standard',
      options: [{ value: 'standard', label: 'Standard' }, { value: 'tax_savings', label: 'Tax Savings Appeal Summary' }] },
    // Her Summary sheet's * / ** marks and footnotes — typed here, kept with the saved report.
    { key: 'summaryMarks', type: 'note', label: 'Summary marks', placeholder: 'e.g. 2024=*, 2025=*, 2026=**', showWhen: (st) => st.layout === 'tax_savings' },
    { key: 'summaryFootnotes', type: 'note', label: 'Summary footnotes', placeholder: 'One footnote per line', lines: 3, showWhen: (st) => st.layout === 'tax_savings' },
  ],
  // Choosing the Tax Savings Appeal Summary turns both savings options to Include, as her totals count Non-NTRG and
  // projected pending savings (Philip, decision B); Standard turns them back. Either can still be switched after.
  onToggle(key, value, state) {
    if (key !== 'layout') return { ...state, [key]: value };
    const an = value === 'tax_savings';
    return { ...state, layout: value, nonNtrg: an ? 'include' : 'exclude', projected: an ? 'include' : 'exclude' };
  },

  columns: PARCEL_YEAR_COLUMNS,
  // Level is hidden by default here only (Philip, 2026-10-10); the other parcel-year reports show it.
  defaultHidden: ['appeal_level'],

  // Opts this report into the client-facing PDF. Outstanding Appeals has no PDF: it is an
  // internal all-years working list, not a per-year client document.
  pdf: true,

  // Level Names ride along for the Tax Savings layout's ValueType; a failed read falls back to the app's labels.
  fetch: async (ctx) => {
    const [scope, ln] = await Promise.all([fetchParcelYearScope(ctx, 'exec summary'), loadLevelNames()]);
    return scope && { ...scope, levelNames: ln.ok ? ln.data : [] };
  },
  // Grouped by tax_year for the per-year tabs and the Summary sheet.
  buildRows: (raw, ctx) => {
    const rows = assertRowFields(buildParcelYearRows(raw, ctx), ['tax_year', 'taxes', 'savings'], 'exec summary');
    return ctx?.filters?.layout === 'tax_savings' ? taxSavingsRows(rows, { levelNames: raw?.levelNames, filters: ctx.filters }) : rows;
  },

  summary(rows) {
    // ⛔ `?? 0` inside the reduce made a tile read "$0" when NO row carried a figure — the same
    // absence-as-answer bug as the average row. sumColumn returns null in that case; a dash says
    // "we don't have this", a $0 says "we checked and it's nothing".
    const money = (k) => {
      const v = sumColumn(rows, k);
      if (v === PENDING) return PENDING;
      const n = toNumber(v);
      // Taxes and savings are money: always cents, as in the table (format.js 'money').
      return n == null ? '—' : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${isEstimated(v) ? '*' : ''}`;
    };
    const accounts = new Set(rows.map((r) => r.account_number || r.__key)).size;
    return [
      { label: 'Accounts', value: accounts.toLocaleString(), sub: `${rows.length.toLocaleString()} account-years` },
      { label: 'Taxes', value: money('taxes') },
      // The decided total; the note says how many are still pending (Philip, 2026-10-08).
      { label: 'Savings', value: money('savings'), sub: decidedNote(rows, 'savings') || undefined },
    ];
  },

  // 6e — HER WORKBOOK SHAPE: one tab per year, plus a Summary tab when more than one year is on
  // screen. A single year gets a single tab and no Summary — a summary of one year is the year.
  //
  // The Summary tab is savings by year plus a rolling AVERAGE_YEARS average, which is the figure
  // she quotes to clients. The average is over the most recent three years PRESENT, not the last
  // three calendar years: averaging in a year the client wasn't ours would understate it, and the
  // representation gate has already removed those rows.
  exportSheets({ rows, columns, totals, footnotes, ctx }) {
    const client = ctx?.clientName || '';
    if (ctx?.filters?.layout === 'tax_savings') {
      return taxSavingsSheets(rows, { clientName: client, marksText: ctx.filters.summaryMarks, footnotesText: ctx.filters.summaryFootnotes });
    }
    const years = [...new Set(rows.map((r) => String(r.tax_year)))].sort();

    const yearSheet = (y) => {
      const yRows = rows.filter((r) => String(r.tax_year) === y);
      // All pending → "Pending"; some → the decided total, labelled "TOTAL — 3 of 5 accounts decided, 2 pending".
      const t = computeTotals(yRows, columns);
      return {
        name: y,
        titleRows: titleRows(client, `${y} Tax Savings Appeal Summary Report`),
        columns,
        rows: yRows,
        totals: t && columns.length ? { ...t, [columns[0].key]: totalLabel(t) } : undefined,
        footnotes,
      };
    };

    if (years.length <= 1) {
      const only = years[0];
      return [only ? yearSheet(only) : {
        name: 'Report',
        titleRows: titleRows(client, 'Property Executive Summary Report'),
        columns, rows, totals: totals || undefined, footnotes,
      }];
    }

    // ⛔ NULL, NOT ZERO, when a year has no figure at all. `?? 0` here made a year with no savings
    // read "$0" on the Summary tab while its detail row read blank — two surfaces of one workbook
    // disagreeing about whether data is missing or the answer is nought.
    // ⛔ AND THE ESTIMATE FLAG SURVIVES. sumColumn returns { value, estimated } when any contributor
    // is estimated; wrapping it in toNumber() threw that away, so a Summary year built entirely from
    // carried-forward rates showed a bare figure while every detail row behind it was asterisked.
    // ⛔ NULL, NOT ZERO, when a year has no resolvable figure at all. sumColumn already returns
    // null in that case; wrapping it in `?? 0` was the bug. A year that DOES resolve to zero —
    // an accepted decision with no reduction — is genuinely 0 and stays 0. The two are different
    // statements and the column has to be able to make both.
    // ⛔ AND THE ESTIMATE FLAG SURVIVES. sumColumn returns { value, estimated } when any
    // contributor is estimated; toNumber() threw that away, so a Summary year built entirely from
    // carried-forward rates showed a bare figure while every detail row behind it was asterisked.
    // ═══ IS THIS YEAR BASELINE? THREE ANSWERS, NOT TWO ═══
    // ⛔ A YEAR IS NOT BASELINE FOR THE WHOLE PORTFOLIO. That was the assumption this marker was
    // first specified under, and it holds only while every parcel shares one First Year. As soon as
    // a client has parcels engaged in different years — Hertz, 21 states, landing exactly that way —
    // a single year is baseline for the parcels that started later and REPRESENTED for the ones
    // already ours. Labelling such a year "baseline" would disclaim appeal work we actually did and
    // understate the engagement, which is worse than the unmarked year this replaces: an unmarked
    // year merely omits a caveat, a wrongly-marked one makes a false statement about our own work.
    //
    // So the grain is: all of the year's rows baseline -> 'Baseline year'; some -> 'Includes baseline years';
    // none -> blank. The middle state is the one that cannot be expressed by marking the year alone,
    // and it is the state every mixed-First-Year client will produce.
    const perYear = years.map((y) => {
      const yRows = rows.filter((r) => String(r.tax_year) === y);
      const nBase = yRows.filter((r) => r.baseline).length;
      const ours = yRows.filter((r) => !r.baseline);
      return {
        year: y,
        savings: sumColumn(yRows, 'savings'),   // the decided total; PENDING when every account is pending
        taxes: sumColumn(yRows, 'taxes'),
        engagement: nBase === 0 ? '' : nBase === yRows.length ? 'Baseline year' : 'Includes baseline years',
        note: decidedNote(yRows, 'savings') || '',
        // COMPLETE = every one of our accounts has a decided savings FIGURE (none pending, none blank) and nothing
        // that year is estimated. Only complete years are averaged (Philip, 2026-10-08).
        complete: ours.length > 0
          && ours.every((r) => toNumber(r.savings) != null && rawValue(r.savings) !== PENDING)
          && !ours.some((r) => isEstimated(r.taxes) || isEstimated(r.savings)),
      };
    });

    // ═══ WHICH YEARS GO INTO THE AVERAGE ═══ (Philip, 2026-10-08)
    // ONLY COMPLETE YEARS — every account decided with a savings figure, nothing estimated — the most recent
    // AVERAGE_YEARS of them. A year with pending appeals would understate the result (her own footnotes excluded
    // such years by hand). The label names the years: "Average of 2022–2024"; with fewer complete years it averages
    // what there is and says so ("Average of 2 years (2023–2024)"). No complete year → a row saying so, no figure:
    // an average over nothing is not zero.
    const recent = perYear.filter((g) => g.complete).slice(-AVERAGE_YEARS);
    const span = (ys) => {
      const n = ys.map(Number);
      const consecutive = n.every((y, i) => i === 0 || y === n[i - 1] + 1);
      return ys.length === 1 ? ys[0] : consecutive ? `${ys[0]}–${ys[ys.length - 1]}` : ys.join(', ');
    };
    const averageRow = [recent.length
      ? {
        year: recent.length === AVERAGE_YEARS ? `Average of ${span(recent.map((g) => g.year))}`
          : `Average of ${recent.length} year${recent.length === 1 ? '' : 's'} (${span(recent.map((g) => g.year))})`,
        savings: recent.reduce((s, g) => s + toNumber(g.savings), 0) / recent.length,
      }
      : { year: 'No complete year to average yet' }];

    const summarySheet = {
      name: 'Summary',
      titleRows: titleRows(client, 'Property Executive Summary Report'),
      // ⛔ YEAR AND TAX SAVINGS ONLY, as hers is. It is a savings report; the tax totals already
      // sit on each year tab, and repeating them here invites the two to be read against each
      // other as though they were different figures.
      // ⛔ A COLUMN, NOT "2025 (baseline)" IN THE YEAR CELL. cellValue writes a non-numeric string
      // in a numeric column as TEXT (the TOTAL / Current Average path), so appending to the year
      // would have rendered — but it turns the year column mixed-type, so Excel sorts and filters
      // 2026 and "2025 (baseline)" as different kinds of thing, and 'Includes baseline years' makes the
      // cell long enough to dominate a two-column sheet. A column also lets the Summary use the
      // SAME word, in the SAME position, as the Note column on every year tab; a reader who
      // meets "Baseline year" on the 2025 tab and "(baseline)" here has to work out they are one claim.
      //
      // ⛔ ONLY WHEN SOMETHING IS ACTUALLY MARKED — and that condition is the same one viewModel
      // uses to attach BASELINE_FOOTNOTE (rows.some(r => r.baseline)). That coupling is the whole
      // fix: the gap being closed was a Summary sheet carrying a footnote saying baseline years are
      // "marked" while marking none of them. Tying the column to the footnote's own condition means
      // the two cannot disagree again. For every client with no baseline year — all of them today —
      // the sheet stays exactly as it is, two columns, unchanged.
      columns: [
        { key: 'year', label: 'Tax Year', type: 'year' },
        ...(perYear.some((g) => g.engagement)
          ? [{ key: 'engagement', label: 'Note', type: 'text' }] : []),
        { key: 'savings', label: 'Tax Savings', type: 'money' },
        // Only when some year is part-decided: "3 of 5 accounts decided, 2 pending" beside its decided total.
        ...(perYear.some((g) => g.note) ? [{ key: 'note', label: 'Accounts', type: 'text' }] : []),
      ],
      rows: perYear,
      totals: (() => {
        const note = decidedNote(rows, 'savings');
        return { year: note ? `TOTAL — ${note}` : 'TOTAL', savings: sumColumn(perYear, 'savings') };
      })(),
      // Years, then TOTAL, then a blank, then the average — her order. trailingRows renders below
      // the totals line and outside the autofilter, so sorting cannot move the average into the data.
      trailingRows: averageRow,
      // ⛔ THE EXTRA NOTE BELONGS TO THIS SHEET ALONE. Summary is the only tab that states a whole
      // year in one row, so it is the only place a part-baseline year can appear with nothing on the
      // row to explain the mix. Appended here, not pushed into the shared footnote list, because a
      // note about “Includes baseline years” on a year tab would describe a marker that tab does not
      // carry — the same rule viewModel applies when it decides whether to emit the base note.
      footnotes: perYear.some((g) => g.engagement === 'Includes baseline years')
        ? [...(footnotes || []), BASELINE_MIXED_FOOTNOTE] : footnotes,
    };

    return [summarySheet, ...years.map(yearSheet)];
  },
};

export default propertyExecutiveSummary;
